<template>
  <fieldset class="ecs-custom-input" :class="{ dense }" :style="FIELDSET_STYLES">
    <legend class="ecs-custom-input-legend" :style="legendStyles">{{ label }} <span v-if="required">*</span></legend>
    <div class="ecs-custom-input-field">
      <!--Renders a Text Field -->
      <v-text-field
        v-if="type === 'text'"
        ref="textField"
        color="inherit"
        background-color="transparent"
        :append-icon="appendIcon"
        :clearable="clearable"
        :disabled="disabled"
        :hide-details="hideDetails"
        :placeholder="placeholder"
        :rules="rules"
        :solo="!hideFieldset"
        :value="value"
        flat
        @blur="onBlur"
        @change="onChange"
        @click:append="onClick"
        @focus="focused = true"
        @input="onInput"
        @keypress="$emit('keypress', $event)"
      >
        <template v-if="showSaveButton" v-slot:append>
          <v-btn icon text @click="$emit('on-save')">
            <v-icon>mdi-content-save</v-icon>
          </v-btn>
        </template>
      </v-text-field>
      <!--Renders a Text Area -->
      <v-textarea
        v-if="type === 'textarea'"
        ref="textArea"
        color="inherit"
        :background-color="getBackgroundColor"
        :append-icon="appendIcon"
        :class="{ 'solo-text-area': hideFieldset, 'form-text-area': !hideFieldset }"
        :clearable="clearable"
        :disabled="disabled"
        :hide-details="hideDetails"
        :placeholder="placeholder"
        :rows="rows"
        :rules="rules"
        :solo="!hideFieldset"
        :value="value"
        auto-grow
        flat
        class="text-area-container"
        @blur="onBlur"
        @change="onChange"
        @click:append="onClick"
        @focus="focused = true"
        @input="onInput"
        @keydown.enter.exact.prevent
      />
      <!--Renders a select dropdown -->
      <v-select
        v-if="type === 'select'"
        ref="select"
        background-color="transparent"
        :clearable="clearable"
        :disabled="disabled"
        :item-text="itemText"
        :item-value="itemValue"
        :items="items"
        :placeholder="placeholder"
        :rules="rules"
        :solo="!hideFieldset"
        :value="value"
        :dense="dense"
        flat
        @blur="focused = false"
        @change="onChange"
        @focus="focused = true"
        @input="onInput"
      />
      <!-- Renders an autocomplete select -->
      <v-autocomplete
        v-if="type === 'autocomplete'"
        ref="autocomplete"
        background-color="transparent"
        :clearable="clearable"
        :disabled="disabled"
        :item-text="itemText"
        :item-value="itemValue"
        :items="items"
        :placeholder="placeholder"
        :rules="rules"
        :solo="!hideFieldset"
        :value="value"
        :menu-props="{ contentClass: 'custom-scrollbar' }"
        flat
        @blur="focused = false"
        @change="onChange"
        @focus="focused = true"
        @input="onInput"
      />
      <!--Renders a select with a tree options -->
      <EcsTreeSelect
        v-if="type === 'treeSelect'"
        ref="treeSelect"
        :clearable="clearable"
        :disabled="disabled"
        :disable-branch-nodes="disableBranchNodes"
        :items="items"
        :placeholder="placeholder"
        :rules="rules"
        :searchable="searchable"
        :value="value"
        close-on-select
        open-on-click
        @blur="focused = false"
        @change="onChange"
        @focus="focused = true"
        @input="onInput"
      >
        <template v-slot:value-label="{ node }">
          <slot name="value-label" :node="node" />
        </template>
      </EcsTreeSelect>
      <!--Renders a date picker-->
      <v-menu
        v-if="type === 'date'"
        ref="menu"
        v-model="openPicker"
        min-width="290px"
        transition="scale-transition"
        :close-on-content-click="false"
        :disabled="disabled"
        offset-y
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            ref="pickerTextField"
            background-color="transparent"
            append-icon="event"
            :clearable="clearable"
            :disabled="disabled"
            :hide-details="hideDetails"
            :placeholder="placeholder"
            :rules="rules"
            :solo="!hideFieldset"
            :value="value"
            flat
            readonly
            v-on="on"
            @blur="focused = false"
            @click:append="openPicker = true"
            @focus="focused = true"
            @keydown="_pickerTextAreaKeyboardInput($event)"
          />
        </template>

        <v-date-picker
          v-if="type === 'date'"
          ref="datePicker"
          v-model="date"
          :format="format"
          :disabled="disabled"
          :min="min"
          no-title
          scrollable
          @change="onChange"
          @input="selectDate"
          @click:date="closeDatePicker"
        >
        </v-date-picker>
      </v-menu>
      <!--Renders a time picker-->
      <EcsTimePicker
        v-if="type === 'time'"
        ref="timePicker"
        :clearable="clearable"
        :disabled="disabled"
        :placeholder="placeholder"
        :format="format"
        :value="userDateTimeDisplay"
        @blur="focused = false"
        @change="onChange"
        @focus="focused = true"
        @input="onInput"
      />
      <!--Renders a switch-->
      <v-switch
        v-if="type === 'flag'"
        v-model="switchValue"
        :label="switchValue ? 'Enabled' : 'Disabled'"
        :disabled="disabled"
        @change="onChange"
      />
    </div>
  </fieldset>
</template>

<script>
import EcsTimePicker from '@/components/ecs-time-picker/EcsTimePicker';
import EcsTreeSelect from '@/components/ecs-tree-select/EcsTreeSelect';

export default {
  name: 'EcsCustomInput',

  components: { EcsTimePicker, EcsTreeSelect },

  props: {
    allowedHours: {
      type: Function,
      default: null
    },
    allowedMinutes: {
      type: Function,
      default: null
    },
    appendIcon: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableBranchNodes: {
      type: Boolean,
      default: false
    },
    format: {
      type: String,
      default: 'HH:mm'
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    hideFieldset: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default() {
        return [];
      }
    },
    itemText: {
      type: String,
      default: 'text'
    },
    itemValue: {
      type: String,
      default: 'value'
    },
    label: {
      type: String,
      default: ''
    },
    min: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    rows: {
      type: Number,
      default: 1
    },
    rules: {
      type: Array,
      default() {
        return [];
      }
    },
    showSaveButton: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    solo: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: [String, Number, Object, Boolean],
      default: null
    },
    dense: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      date: this.value,
      focused: false,
      lastTimeInputKeyPressed: null,
      openPicker: false,
      valid: true,
      switchValue: false
    };
  },

  computed: {
    getBackgroundColor() {
      return this.hideFieldset ? 'surface' : 'transparent';
    },
    legendStyles() {
      return {
        'margin-left': this.hideFieldset ? '0' : '16px',
        padding: this.hideFieldset ? '0' : '0 8px',
        color: this.valid ? 'inherit' : this.$vuetify.theme.themes.dark.error
      };
    },
    userDateTimeDisplay() {
      return this.$options.filters.EcsUserTimeFormat(this.value, false);
    },
    FIELDSET_STYLES() {
      let borderColor = '';
      let zIndex = '';
      let backgroundColor = '';
      let opacity = '1';
      let height = '60px';
      const width = '100%';

      if (this.hideFieldset) {
        borderColor = 'transparent';
      } else if (!this.valid) {
        zIndex = 10;
        borderColor = this.$vuetify.theme.themes.dark.error;
      }

      if (this.disabled) {
        backgroundColor = 'transparent';
        opacity = '0.35';
      }

      if (this.type === 'textarea') {
        height = 'auto';
      }

      return {
        borderColor,
        // animation,
        zIndex,
        backgroundColor,
        opacity,
        height,
        width
      };
    }
  },

  watch: {
    focused() {
      this.$emit('focus', this.focused);
    },
    openPicker(open) {
      if (this.type === 'date') {
        const $datePicker = this._getDatePicker();
        if ($datePicker) {
          if (open === true) {
            // Note: I hate myself for this. When does Vuetify's <transition> end?
            setTimeout(function() {
              $datePicker.querySelector('button').focus();
            }, 250);
          } else {
            this.$refs.pickerTextField.focus();
          }
        }
      }
    },
    value(newValue) {
      this.validate(newValue);
    }
  },

  created() {
    if (this.type === 'flag') {
      this.switchValue = this.value;
    }
    this.updateStyles();
  },

  methods: {
    closeDatePicker() {
      this.openPicker = false;
    },

    onBlur(value) {
      this.focused = false;
      this.$emit('blur', value);
    },
    onChange(value) {
      this.$emit('change', value);
    },
    onClick() {
      this.$emit('click-icon');
    },
    onInput(value) {
      this.$emit('input', value);
    },
    selectDate(e) {
      this.$emit('input', this.date);
    },
    updateStyles() {
      const newInputStylesheet = document.createElement('style');
      newInputStylesheet.id = 'ecs-custom-input-styles';
      newInputStylesheet.innerHTML = `
        /* Animations */
        @-webkit-keyframes shake-horizontal {
          0%,
          100% {
            -webkit-transform: translateX(0);
                    transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70% {
            -webkit-transform: translateX(-10px);
                    transform: translateX(-10px);
          }
          20%,
          40%,
          60% {
            -webkit-transform: translateX(10px);
                    transform: translateX(10px);
          }
          80% {
            -webkit-transform: translateX(8px);
                    transform: translateX(8px);
          }
          90% {
            -webkit-transform: translateX(-8px);
                    transform: translateX(-8px);
          }
        }
        @keyframes shake-horizontal {
          0%,
          100% {
            -webkit-transform: translateX(0);
                    transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70% {
            -webkit-transform: translateX(-10px);
                    transform: translateX(-10px);
          }
          20%,
          40%,
          60% {
            -webkit-transform: translateX(10px);
                    transform: translateX(10px);
          }
          80% {
            -webkit-transform: translateX(8px);
                    transform: translateX(8px);
          }
          90% {
            -webkit-transform: translateX(-8px);
                    transform: translateX(-8px);
          }
        }

      `;

      const oldInputStyles = document.getElementById('ecs-custom-input-styles');
      if (oldInputStyles) {
        oldInputStyles.remove();
      }

      document.head.appendChild(newInputStylesheet);
    },
    validate(value) {
      Object.values(this.$refs).forEach(field => {
        if (field && typeof field.validate === 'function') {
          this.valid = field.validate(true, value);
        }
      });
      return this.valid;
    },
    _getDatePicker() {
      let $datePicker = null;
      if (this.$refs.datePicker) {
        $datePicker = this.$refs.datePicker.$el;
      }

      return $datePicker;
    },
    _pickerTextAreaKeyboardInput(keydownEvent) {
      // Allow Enter OR Space to open the control
      if (['Enter', ' '].includes(keydownEvent.key)) {
        this.openPicker = true;
      }
    }
  }
};
</script>

<style scoped>
fieldset {
  border-radius: 8px;
}

legend {
  text-align: left;
}

.ecs-custom-input-field .ecs-time-picker {
  padding: 0 12px;
}
</style>

<style>
.ecs-custom-input-field .v-text-field {
  border-radius: 8px;
}

.ecs-custom-input-field textarea {
  min-height: 40px;
  max-width: calc(100% - 24px);
}

.ecs-custom-input-field .v-input--switch {
  margin-top: unset;
  margin-left: 20px;
}

.ecs-custom-input-field .text-area-container .v-input__append-inner {
  position: absolute;
  top: 8px;
  right: 8px;
}

.ecs-custom-input.dense {
  height: 56px !important;
  margin-bottom: 20px;
}

.ecs-custom-input.dense .ecs-custom-input-legend {
  font-size: 12px;
  opacity: 0.75;
}

.ecs-custom-input .v-input {
  font-size: 14px;
}
.ecs-custom-input .v-select__selection.v-select__selection--comma {
  position: absolute;
  top: 0;
  max-width: 80%;
  font-size: 14px;
}

.ecs-custom-input .v-text-field.v-text-field--solo .v-input__control {
  min-height: auto !important;
}

.solo-text-area {
  display: inline-flex;
  height: auto;
  width: 100%;
  position: relative;
  border-radius: 8px;
  z-index: 1;
  font-size: 16px;
  outline: none;
}
.solo-text-area textarea {
  padding: 12px 16px 12px 12px;
}
.form-text-area textarea {
  margin-top: 8px !important;
  padding: 0;
}
.form-text-area .v-input__append-inner {
  margin-top: 0 !important;
}
</style>
